import mapboxgl, { Expression } from 'mapbox-gl'

import { IAPI } from 'src/api'
import { Provider, Stops, Stops_LineTypeEnum } from 'src/api/gen'
import { INFRASTRUCTURE_STOPS_ICONS, INFRASTRUCTURE_STOPS_PRIORITY } from 'src/components/Infrastructure'

import { addMapImage } from './utils'

const SOURCE_NAME = 'stops'

export interface IClickedStopPoint {
  coordinates: mapboxgl.LngLat
  provider?: Provider
  stop: Stops
}

export function stopsToGeoJSON(stopList: Stops[], stopsToProperties: (s: any) => { [key: string]: any } = (s) => s) {
  const features = stopList.map((stop: Stops) => ({
    type: 'Feature',
    geometry: {
      coordinates: stop.gps_point || [0, 0],
      type: 'Point',
    },
    properties: stopsToProperties(stop),
  }))

  return {
    type: 'FeatureCollection',
    features,
  }
}

export async function addStopsToMap(
  map: mapboxgl.Map,
  assets: Stops[],
  setClickedStop?: (stop: IClickedStopPoint | undefined) => void,
  onLoaded?: () => void
) {
  map.addSource(SOURCE_NAME, {
    type: 'geojson',
    data: stopsToGeoJSON(assets),
  } as any)

  await addMapImage(map, INFRASTRUCTURE_STOPS_ICONS.bus, 'bus')
  await addMapImage(map, INFRASTRUCTURE_STOPS_ICONS.subway, 'subway')
  await addMapImage(map, INFRASTRUCTURE_STOPS_ICONS.tram, 'tram')
  await addMapImage(map, INFRASTRUCTURE_STOPS_ICONS.train, 'train')

  map.addLayer({
    id: 'stops-layer',
    type: 'symbol',
    source: SOURCE_NAME,
    layout: {
      'icon-image': [
        'match',
        ['get', 'line_type'],
        ...Array.prototype.concat(...Object.keys(INFRASTRUCTURE_STOPS_ICONS).map((key) => [key, key])),
        'unspecified',
      ] as Expression,
      'icon-size': 0.5,
      'icon-allow-overlap': false,
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'to-number',
        ['get', 'priority'],
        ...Array.prototype.concat(...Object.keys(INFRASTRUCTURE_STOPS_PRIORITY).map((key) => [key, key])),
      ],
    },
  })

  if (setClickedStop) {
    const displayStopsPopup = (layer: string) => async (e: any) => {
      const stops = map
        ? map.queryRenderedFeatures(e.point, {
            layers: [layer],
          })
        : []
      if (stops.length && stops[0].properties) {
        const stop = stops[0].properties
        setClickedStop({
          coordinates: e.lngLat,
          stop: stop as Stops,
        })
      }
    }
    map.on('click', 'stops-layer', displayStopsPopup('stops-layer'))
  }

  // When all loading operations are done, the map enters an idle state
  if (onLoaded) {
    map.on('idle', onLoaded)
  }
}

export function clearStopsMap(map: mapboxgl.Map) {
  if (map.getSource(SOURCE_NAME)) {
    map.removeLayer('stops-layer')
    map.removeSource(SOURCE_NAME)
  }
}

export async function fetchAndUpdateStops(
  api: IAPI,
  lineTypes: Stops_LineTypeEnum[],
  map: mapboxgl.Map | undefined,
  setClickedStopPoint: (stop: IClickedStopPoint | undefined) => void,
  setAreStopsLoading: (loading: boolean) => void,
  onMapInitialized?: () => void,
  signal?: AbortSignal
) {
  if (lineTypes.length === 0) {
    if (map) {
      clearStopsMap(map)
    }
    setAreStopsLoading(false)
    return
  }

  try {
    setAreStopsLoading(true)
    const response = await api.stops.stopsList({ line_type: lineTypes, options: { signal } })
    setAreStopsLoading(false)

    if (map && !signal?.aborted) {
      clearStopsMap(map)
      addStopsToMap(map, response.results, setClickedStopPoint, onMapInitialized)
    }
  } catch (exception) {
    if (!signal?.aborted) {
      console.error(exception)
    }
  }
}
